import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Paintbrush, Monitor, Smartphone, Check, Eye, Upload, Trash2, Image, Plus, ChevronUp, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { THEME_LIST, getTheme } from '../lib/themes';
import { PhoneInput } from '../components/PhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Apariencia = () => {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [previewDevice, setPreviewDevice] = useState('desktop');
    const [previewTheme, setPreviewTheme] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [marcas, setMarcas] = useState([]);
    const [marcasLoading, setMarcasLoading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const res = await axios.get(`${API_URL}/api/tienda/config`);
                setConfig(res.data);
                setPreviewTheme(getTheme(res.data.tema_activo));
            } catch { toast.error('Error al cargar configuracion'); }
            finally { setLoading(false); }
        };
        fetchConfig();
        // Cargar marcas
        (async () => {
            setMarcasLoading(true);
            try { const r = await axios.get(`${API_URL}/api/marcas`); setMarcas(r.data || []); }
            catch { /* ignore */ }
            finally { setMarcasLoading(false); }
        })();
    }, []);

    const selectTheme = (themeId) => {
        const theme = getTheme(themeId);
        setPreviewTheme(theme);
        setConfig(prev => ({
            ...prev,
            tema_activo: themeId,
            hero_tagline: prev.hero_tagline || theme.hero.tagline,
            hero_title_1: prev.hero_title_1 || theme.hero.title1,
            hero_title_2: prev.hero_title_2 || theme.hero.title2,
            hero_subtitle: prev.hero_subtitle || theme.hero.subtitle,
            hero_cta: prev.hero_cta || theme.hero.cta,
        }));
    };

    const applyThemeDefaults = () => {
        if (!previewTheme) return;
        setConfig(prev => ({
            ...prev,
            hero_tagline: previewTheme.hero.tagline,
            hero_title_1: previewTheme.hero.title1,
            hero_title_2: previewTheme.hero.title2,
            hero_subtitle: previewTheme.hero.subtitle,
            hero_cta: previewTheme.hero.cta,
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(`${API_URL}/api/tienda/config`, config);
            toast.success('Tema aplicado correctamente');
        } catch { toast.error('Error al guardar'); }
        finally { setSaving(false); }
    };

    const handleUploadHeroImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/api/tienda/hero-image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
            });
            setConfig(prev => ({ ...prev, hero_image: res.data.hero_image }));
            toast.success('Imagen subida correctamente');
        } catch { toast.error('Error al subir imagen'); }
        finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
    };

    const handleUploadCategoriaImage = async (slug, file) => {
        if (!file) return;
        try {
            const formData = new FormData();
            formData.append('file', file);
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/api/tienda/categoria-image`, formData, {
                headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
            });
            const url = res.data.image_url;
            const cats = [...(config.categorias_config || [])];
            const idx = cats.findIndex(c => c.slug === slug);
            if (idx >= 0) cats[idx] = { ...cats[idx], image: url };
            setConfig({ ...config, categorias_config: cats });
            toast.success('Imagen subida');
        } catch { toast.error('Error al subir imagen'); }
    };

    const handleDeleteHeroImage = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/api/tienda/hero-image`, { headers: { Authorization: `Bearer ${token}` } });
            setConfig(prev => ({ ...prev, hero_image: '' }));
            toast.success('Imagen eliminada');
        } catch { toast.error('Error al eliminar imagen'); }
    };

    if (loading || !config) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div></div>;

    const t = previewTheme || getTheme('denim_premium');

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="apariencia-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100">Apariencia de Tienda</h1>
                    <p className="text-zinc-400 mt-1">Cambia el tema visual de tu tienda sin tocar codigo</p>
                </div>
                <div className="flex gap-2">
                    <a href="/tienda" target="_blank" rel="noopener noreferrer">
                        <Button variant="outline"><Eye className="w-4 h-4 mr-2" /> Ver Tienda</Button>
                    </a>
                    <Button onClick={handleSave} disabled={saving} className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="btn-save-theme">
                        {saving ? 'Guardando...' : 'Aplicar Tema'}
                    </Button>
                </div>
            </div>

            {/* Theme Grid */}
            <div>
                <h2 className="text-lg font-semibold text-zinc-200 mb-3 flex items-center gap-2">
                    <Paintbrush className="w-5 h-5 text-amber-400" /> Temas Prediseñados
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {THEME_LIST.map(theme => (
                        <button key={theme.id} onClick={() => selectTheme(theme.id)}
                            className={`relative p-0.5 rounded-xl transition-all ${config.tema_activo === theme.id ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-zinc-900' : 'hover:ring-1 hover:ring-zinc-600'}`}
                            data-testid={`theme-${theme.id}`}>
                            <div className="rounded-xl overflow-hidden">
                                <div className="h-20 w-full" style={{ background: theme.preview }} />
                                <div className="p-2.5 bg-zinc-800">
                                    <p className="text-xs font-semibold text-zinc-200 truncate">{theme.name}</p>
                                    <p className="text-[10px] text-zinc-500 truncate">{theme.description}</p>
                                </div>
                            </div>
                            {config.tema_activo === theme.id && (
                                <div className="absolute top-2 right-2 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center">
                                    <Check className="w-3 h-3 text-zinc-900" />
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: Config */}
                <div className="space-y-5">
                    <Card className="card">
                        <CardContent className="p-5 space-y-4">
                            <div className="flex justify-between items-center">
                                <h3 className="font-semibold text-zinc-200">Contenido del Hero</h3>
                                <Button size="sm" variant="ghost" onClick={applyThemeDefaults} className="text-xs text-amber-400">Usar textos del tema</Button>
                            </div>
                            <div className="form-group"><Label className="form-label">Etiqueta superior</Label>
                                <Input value={config.hero_tagline || ''} onChange={e => setConfig({ ...config, hero_tagline: e.target.value })} placeholder="Ej: NUEVA COLECCION" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="form-group"><Label className="form-label">Titulo linea 1</Label>
                                    <Input value={config.hero_title_1 || ''} onChange={e => setConfig({ ...config, hero_title_1: e.target.value })} placeholder="DENIM" />
                                </div>
                                <div className="form-group"><Label className="form-label">Titulo linea 2</Label>
                                    <Input value={config.hero_title_2 || ''} onChange={e => setConfig({ ...config, hero_title_2: e.target.value })} placeholder="PREMIUM" />
                                </div>
                            </div>
                            <div className="form-group"><Label className="form-label">Subtitulo</Label>
                                <Input value={config.hero_subtitle || ''} onChange={e => setConfig({ ...config, hero_subtitle: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="form-group"><Label className="form-label">Texto del boton</Label>
                                    <Input value={config.hero_cta || ''} onChange={e => setConfig({ ...config, hero_cta: e.target.value })} />
                                </div>
                                <div className="form-group"><Label className="form-label">Banner promocional</Label>
                                    <Input value={config.banner_text || ''} onChange={e => setConfig({ ...config, banner_text: e.target.value })} placeholder="Ej: ENVIO GRATIS" />
                                </div>
                            </div>
                            <div className="border-t border-zinc-700 pt-4 mt-2">
                                <div className="flex items-center justify-between mb-1">
                                    <h4 className="text-sm font-semibold text-zinc-200 flex items-center gap-2">
                                        <span className="text-blue-400">●</span> Categorias y Hero dinamico
                                    </h4>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                            const cats = [...(config.categorias_config || [])];
                                            // Generar slug unico
                                            let n = 1;
                                            let newSlug = `categoria-${n}`;
                                            const existing = new Set(cats.map(c => c.slug));
                                            while (existing.has(newSlug)) { n++; newSlug = `categoria-${n}`; }
                                            cats.push({ slug: newSlug, label: 'Nueva Categoria', title: '', tagline: '', image: '' });
                                            setConfig({ ...config, categorias_config: cats });
                                            toast.success('Categoria agregada. Personaliza nombre y slug.');
                                        }}
                                        className="text-xs"
                                        data-testid="btn-agregar-categoria"
                                    >
                                        <Plus className="w-3.5 h-3.5 mr-1" /> Agregar categoria
                                    </Button>
                                </div>
                                <p className="text-xs text-zinc-500 mb-3">Cada tab del catalogo puede tener su propia imagen, titulo y tagline. El <strong>slug</strong> es el ID interno (kebab-case, ej. <code>bermuda</code>). El <strong>nombre</strong> es lo que ve el cliente. Usa las flechas <ChevronUp className="inline w-3 h-3" /><ChevronDown className="inline w-3 h-3" /> para cambiar el <strong>orden</strong> en que aparecen en la tienda.</p>
                                <div className="space-y-4">
                                    {(config.categorias_config || []).map((cat, idx) => {
                                        const isProtected = ['cholo', 'vaquero', 'nino', 'playeras', 'estampado'].includes(cat.slug);
                                        const totalCats = (config.categorias_config || []).length;
                                        const moveCategoria = (from, to) => {
                                            if (to < 0 || to >= totalCats) return;
                                            const cats = [...config.categorias_config];
                                            const [moved] = cats.splice(from, 1);
                                            cats.splice(to, 0, moved);
                                            setConfig({ ...config, categorias_config: cats });
                                        };
                                        return (
                                        <div key={`${cat.slug}-${idx}`} className="p-3 rounded-lg border border-zinc-700 bg-zinc-900/30">
                                            <div className="flex items-center justify-between mb-2 gap-2">
                                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                                    {/* Botones de orden */}
                                                    <div className="flex flex-col gap-0.5 shrink-0">
                                                        <button
                                                            type="button"
                                                            disabled={idx === 0}
                                                            onClick={() => moveCategoria(idx, idx - 1)}
                                                            className="w-5 h-4 flex items-center justify-center rounded text-zinc-400 hover:text-amber-400 hover:bg-zinc-800 disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-zinc-400 transition"
                                                            title="Subir"
                                                            data-testid={`btn-subir-cat-${cat.slug}`}
                                                        >
                                                            <ChevronUp className="w-3.5 h-3.5" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            disabled={idx === totalCats - 1}
                                                            onClick={() => moveCategoria(idx, idx + 1)}
                                                            className="w-5 h-4 flex items-center justify-center rounded text-zinc-400 hover:text-amber-400 hover:bg-zinc-800 disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-zinc-400 transition"
                                                            title="Bajar"
                                                            data-testid={`btn-bajar-cat-${cat.slug}`}
                                                        >
                                                            <ChevronDown className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                    <span className="text-[10px] font-mono text-zinc-500 w-5 text-center shrink-0">{idx + 1}</span>
                                                    <p className="text-sm font-bold text-zinc-100 capitalize truncate">{cat.label || cat.slug}</p>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    {isProtected && <span className="text-[9px] text-zinc-500 uppercase tracking-wider">Default</span>}
                                                    {!isProtected && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                if (!window.confirm(`Eliminar la categoria "${cat.label || cat.slug}"? Los productos no se borran, solo la pestaña.`)) return;
                                                                const cats = (config.categorias_config || []).filter((_, i) => i !== idx);
                                                                setConfig({ ...config, categorias_config: cats });
                                                                toast.success('Categoria eliminada');
                                                            }}
                                                            className="text-red-400 hover:text-red-300"
                                                            data-testid={`btn-eliminar-cat-${cat.slug}`}
                                                            title="Eliminar categoria"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                                <div>
                                                    <Label className="form-label">Nombre de pestaña (visible)</Label>
                                                    <Input
                                                        value={cat.label || ''}
                                                        onChange={(e) => {
                                                            const cats = [...config.categorias_config];
                                                            cats[idx] = { ...cats[idx], label: e.target.value };
                                                            setConfig({ ...config, categorias_config: cats });
                                                        }}
                                                        placeholder="Ej. Bermudas"
                                                        data-testid={`cat-label-${cat.slug}`}
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="form-label">Slug / ID interno {isProtected && <span className="text-[10px] text-zinc-500">(no editable)</span>}</Label>
                                                    <Input
                                                        value={cat.slug || ''}
                                                        disabled={isProtected}
                                                        onChange={(e) => {
                                                            const cleanSlug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
                                                            const cats = [...config.categorias_config];
                                                            cats[idx] = { ...cats[idx], slug: cleanSlug };
                                                            setConfig({ ...config, categorias_config: cats });
                                                        }}
                                                        placeholder="bermuda"
                                                        className="font-mono text-xs"
                                                        data-testid={`cat-slug-${cat.slug}`}
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="form-label">Titulo del hero</Label>
                                                    <Input
                                                        value={cat.title || ''}
                                                        onChange={(e) => {
                                                            const cats = [...config.categorias_config];
                                                            cats[idx] = { ...cats[idx], title: e.target.value };
                                                            setConfig({ ...config, categorias_config: cats });
                                                        }}
                                                        placeholder="Ej. BERMUDAS CARGO"
                                                        data-testid={`cat-title-${cat.slug}`}
                                                    />
                                                </div>
                                                <div>
                                                    <Label className="form-label">Tagline / Subtitulo</Label>
                                                    <Input
                                                        value={cat.tagline || ''}
                                                        onChange={(e) => {
                                                            const cats = [...config.categorias_config];
                                                            cats[idx] = { ...cats[idx], tagline: e.target.value };
                                                            setConfig({ ...config, categorias_config: cats });
                                                        }}
                                                        placeholder="Comodidad para todo el verano"
                                                        data-testid={`cat-tagline-${cat.slug}`}
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <Label className="form-label">Imagen de fondo del hero</Label>
                                                <div className="flex items-center gap-2 mt-1">
                                                    {cat.image ? (
                                                        <div className="relative w-24 h-16 rounded overflow-hidden border border-zinc-700">
                                                            <img src={`${API_URL}${cat.image}`} alt="" className="w-full h-full object-cover" />
                                                            <button onClick={() => {
                                                                const cats = [...config.categorias_config];
                                                                cats[idx] = { ...cats[idx], image: '' };
                                                                setConfig({ ...config, categorias_config: cats });
                                                            }} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center">
                                                                <Trash2 className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="w-24 h-16 rounded border border-dashed border-zinc-700 flex items-center justify-center">
                                                            <Image className="w-5 h-5 text-zinc-600" />
                                                        </div>
                                                    )}
                                                    <label className="cursor-pointer">
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            className="hidden"
                                                            onChange={(e) => handleUploadCategoriaImage(cat.slug, e.target.files?.[0])}
                                                            data-testid={`cat-upload-${cat.slug}`}
                                                        />
                                                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-xs font-bold text-zinc-100">
                                                            <Upload className="w-3 h-3" /> {cat.image ? 'Cambiar' : 'Subir'}
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="border-t border-zinc-700 pt-4 mt-2">
                                <h4 className="text-sm font-semibold text-zinc-200 mb-1 flex items-center gap-2">
                                    <span className="text-purple-400">●</span> Tipos de venta en la tienda online
                                </h4>
                                <p className="text-xs text-zinc-500 mb-3">Controla qué opciones de compra aparecen en la tienda pública. Útil cuando solo quieres vender por paquete (mayoreo) o desactivar ventas individuales temporalmente. <strong>NO afecta el POS interno</strong>, solo lo que ven los clientes en la web.</p>
                                <div className="space-y-3">
                                    {[
                                        { key: 'venta_pieza_activa', label: 'Venta por Pieza', desc: 'Cliente compra 1 unidad a la vez (precio menudeo).', color: 'emerald' },
                                        { key: 'venta_paquete_activa', label: 'Venta por Paquete', desc: 'Cliente compra paquete completo surtido (precio mayoreo).', color: 'amber' },
                                        { key: 'venta_corrida_activa', label: 'Venta por Corrida', desc: 'Cliente compra media corrida (mitad de paquete, precio intermedio).', color: 'blue' },
                                    ].map(opt => {
                                        const isActive = config[opt.key] !== false;
                                        return (
                                            <div key={opt.key} className={`flex items-center justify-between gap-3 p-3 rounded-lg border transition ${isActive ? `border-${opt.color}-500/40 bg-${opt.color}-500/5` : 'border-zinc-700 bg-zinc-900/40'}`}>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`w-2 h-2 rounded-full ${isActive ? `bg-${opt.color}-400` : 'bg-zinc-600'}`} />
                                                        <p className="text-sm font-bold text-zinc-100">{opt.label}</p>
                                                        <span className={`text-[10px] uppercase font-bold tracking-wider ${isActive ? `text-${opt.color}-400` : 'text-zinc-500'}`}>
                                                            {isActive ? 'Activa' : 'Desactivada'}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-zinc-500 mt-0.5">{opt.desc}</p>
                                                </div>
                                                <Switch
                                                    checked={isActive}
                                                    onCheckedChange={(v) => setConfig({ ...config, [opt.key]: v })}
                                                    data-testid={`toggle-${opt.key}`}
                                                />
                                            </div>
                                        );
                                    })}
                                    {!(config.venta_pieza_activa !== false || config.venta_paquete_activa !== false || config.venta_corrida_activa !== false) && (
                                        <div className="p-2 rounded bg-red-500/10 border border-red-500/30 text-xs text-red-300">
                                            ⚠️ Tienes los 3 tipos desactivados. Los clientes no podrán comprar en la tienda online. Activa al menos uno.
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="border-t border-zinc-700 pt-4 mt-2">
                                <div className="flex items-center justify-between mb-1">
                                    <h4 className="text-sm font-semibold text-zinc-200 flex items-center gap-2">
                                        <span className="text-pink-400">●</span> Marcas (banner, logo, descripcion)
                                    </h4>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                            const m = [...(marcas || [])];
                                            m.push({ slug: `marca-${Date.now().toString(36)}`, nombre: 'Nueva Marca', descripcion: '', logo_url: '', banner_url: '', orden: m.length, activa: true });
                                            setMarcas(m);
                                        }}
                                        className="text-xs"
                                        data-testid="btn-agregar-marca"
                                    >
                                        + Agregar marca
                                    </Button>
                                </div>
                                <p className="text-xs text-zinc-500 mb-3">Configura el banner, logo y descripcion de cada marca. Los productos cuyo campo <strong>marca</strong> coincida con el <strong>nombre</strong> de aqui usaran este banner y logo. Si no configuras una marca, se muestra estilo simple con el nombre.</p>
                                {marcasLoading ? (
                                    <p className="text-xs text-zinc-500">Cargando marcas...</p>
                                ) : (marcas || []).length === 0 ? (
                                    <p className="text-xs text-zinc-500 italic">Aun no hay marcas configuradas. Usa &quot;+ Agregar marca&quot; para crear una.</p>
                                ) : (
                                    <div className="space-y-3">
                                        {(marcas || []).map((m, idx) => (
                                            <div key={m.slug || idx} className="p-3 rounded-lg border border-zinc-700 bg-zinc-900/30 space-y-2">
                                                <div className="flex items-center justify-between gap-2">
                                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                                        {m.logo_url && <img src={m.logo_url} alt={m.nombre} className="w-8 h-8 rounded object-contain bg-white p-0.5" />}
                                                        <p className="text-sm font-bold text-zinc-100 truncate">{m.nombre || m.slug}</p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={async () => {
                                                            if (!window.confirm(`Eliminar marca "${m.nombre || m.slug}"?`)) return;
                                                            try {
                                                                if (m.slug) await axios.delete(`${API_URL}/api/marcas/${m.slug}`);
                                                                setMarcas((marcas || []).filter((_, i) => i !== idx));
                                                                toast.success('Marca eliminada');
                                                            } catch { toast.error('Error al eliminar'); }
                                                        }}
                                                        className="text-red-400 hover:text-red-300"
                                                        data-testid={`btn-eliminar-marca-${m.slug}`}
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                    <div>
                                                        <Label className="form-label">Nombre (visible en banner)</Label>
                                                        <Input value={m.nombre || ''} onChange={(e) => { const x = [...marcas]; x[idx] = { ...x[idx], nombre: e.target.value }; setMarcas(x); }} placeholder="Joker" data-testid={`marca-nombre-${m.slug}`} />
                                                    </div>
                                                    <div>
                                                        <Label className="form-label">Slug (ID interno)</Label>
                                                        <Input value={m.slug || ''} onChange={(e) => { const x = [...marcas]; x[idx] = { ...x[idx], slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }; setMarcas(x); }} placeholder="joker" className="font-mono text-xs" data-testid={`marca-slug-${m.slug}`} />
                                                    </div>
                                                </div>
                                                <div>
                                                    <Label className="form-label">Descripcion corta</Label>
                                                    <Input value={m.descripcion || ''} onChange={(e) => { const x = [...marcas]; x[idx] = { ...x[idx], descripcion: e.target.value }; setMarcas(x); }} placeholder="Coleccion Premium - Estilo urbano" data-testid={`marca-desc-${m.slug}`} />
                                                </div>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                    <div>
                                                        <Label className="form-label">Logo</Label>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            {m.logo_url ? (
                                                                <div className="relative w-14 h-14 rounded overflow-hidden border border-zinc-700 bg-white p-1 shrink-0">
                                                                    <img src={m.logo_url} alt="" className="w-full h-full object-contain" />
                                                                    <button type="button" onClick={() => { const x = [...marcas]; x[idx] = { ...x[idx], logo_url: '' }; setMarcas(x); }} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-[10px]">×</button>
                                                                </div>
                                                            ) : (
                                                                <div className="w-14 h-14 rounded border border-dashed border-zinc-700 flex items-center justify-center shrink-0">
                                                                    <Image className="w-5 h-5 text-zinc-600" />
                                                                </div>
                                                            )}
                                                            <label className="cursor-pointer">
                                                                <input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    className="hidden"
                                                                    onChange={async (e) => {
                                                                        const file = e.target.files?.[0];
                                                                        if (!file) return;
                                                                        try {
                                                                            const fd = new FormData();
                                                                            fd.append('file', file);
                                                                            const tk = localStorage.getItem('token');
                                                                            const r = await axios.post(`${API_URL}/api/marcas/upload?tipo=logo`, fd, { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${tk}` } });
                                                                            const x = [...marcas];
                                                                            x[idx] = { ...x[idx], logo_url: r.data.image_url };
                                                                            setMarcas(x);
                                                                            toast.success('Logo subido');
                                                                        } catch { toast.error('Error al subir logo'); }
                                                                    }}
                                                                    data-testid={`marca-logo-upload-${m.slug}`}
                                                                />
                                                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-xs font-bold text-zinc-100">
                                                                    <Upload className="w-3 h-3" /> {m.logo_url ? 'Cambiar' : 'Subir logo'}
                                                                </span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <Label className="form-label">Banner (fondo)</Label>
                                                        <div className="flex items-center gap-2 mt-1">
                                                            {m.banner_url ? (
                                                                <div className="relative w-20 h-14 rounded overflow-hidden border border-zinc-700 shrink-0">
                                                                    <img src={m.banner_url} alt="" className="w-full h-full object-cover" />
                                                                    <button type="button" onClick={() => { const x = [...marcas]; x[idx] = { ...x[idx], banner_url: '' }; setMarcas(x); }} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-[10px]">×</button>
                                                                </div>
                                                            ) : (
                                                                <div className="w-20 h-14 rounded border border-dashed border-zinc-700 flex items-center justify-center shrink-0">
                                                                    <Image className="w-5 h-5 text-zinc-600" />
                                                                </div>
                                                            )}
                                                            <label className="cursor-pointer">
                                                                <input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    className="hidden"
                                                                    onChange={async (e) => {
                                                                        const file = e.target.files?.[0];
                                                                        if (!file) return;
                                                                        try {
                                                                            const fd = new FormData();
                                                                            fd.append('file', file);
                                                                            const tk = localStorage.getItem('token');
                                                                            const r = await axios.post(`${API_URL}/api/marcas/upload?tipo=banner`, fd, { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${tk}` } });
                                                                            const x = [...marcas];
                                                                            x[idx] = { ...x[idx], banner_url: r.data.image_url };
                                                                            setMarcas(x);
                                                                            toast.success('Banner subido');
                                                                        } catch { toast.error('Error al subir banner'); }
                                                                    }}
                                                                    data-testid={`marca-banner-upload-${m.slug}`}
                                                                />
                                                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-zinc-800 hover:bg-zinc-700 text-xs font-bold text-zinc-100">
                                                                    <Upload className="w-3 h-3" /> {m.banner_url ? 'Cambiar' : 'Subir banner'}
                                                                </span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        <Button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    for (const m of marcas) {
                                                        if (!m.slug || !m.nombre) continue;
                                                        await axios.put(`${API_URL}/api/marcas/${m.slug}`, {
                                                            slug: m.slug, nombre: m.nombre,
                                                            descripcion: m.descripcion || '',
                                                            logo_url: m.logo_url || '',
                                                            banner_url: m.banner_url || '',
                                                            orden: m.orden || 0, activa: m.activa !== false,
                                                        });
                                                    }
                                                    toast.success('Marcas guardadas');
                                                } catch { toast.error('Error al guardar marcas'); }
                                            }}
                                            className="bg-pink-600 hover:bg-pink-700 text-white text-xs"
                                            data-testid="btn-guardar-marcas"
                                        >
                                            Guardar marcas
                                        </Button>
                                    </div>
                                )}
                            </div>

                            <div className="border-t border-zinc-700 pt-4 mt-2">
                                <h4 className="text-sm font-semibold text-zinc-200 mb-3 flex items-center gap-2">
                                    <span className="text-emerald-400">●</span> WhatsApp para pedidos
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="form-group">
                                        <Label className="form-label">Numero de WhatsApp</Label>
                                        <PhoneInput
                                            value={config.whatsapp_numero || ''}
                                            onChange={(v) => setConfig({ ...config, whatsapp_numero: v })}
                                            placeholder="5512345678"
                                            testId="apariencia-whatsapp"
                                        />
                                        <p className="text-xs text-zinc-500 mt-1">Default Mexico (+52). Cambia la bandera para otro pais.</p>
                                    </div>
                                    <div className="form-group">
                                        <Label className="form-label">Mensaje predeterminado</Label>
                                        <Input
                                            value={config.whatsapp_mensaje || ''}
                                            onChange={e => setConfig({ ...config, whatsapp_mensaje: e.target.value })}
                                            placeholder="Hola! Me interesa hacer un pedido"
                                            data-testid="input-whatsapp-mensaje"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-zinc-700 pt-4 mt-2">
                                <h4 className="text-sm font-semibold text-zinc-200 mb-3 flex items-center gap-2">
                                    <span className="text-amber-400">●</span> Ubicacion de la tienda
                                </h4>
                                <div className="space-y-3">
                                    <div className="form-group">
                                        <Label className="form-label">Direccion fisica</Label>
                                        <Input
                                            value={config.tienda_direccion || ''}
                                            onChange={e => setConfig({ ...config, tienda_direccion: e.target.value })}
                                            placeholder="Calle, numero, colonia, ciudad, estado, CP"
                                            data-testid="input-tienda-direccion"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="form-label">Horarios de atencion</Label>
                                        <Input
                                            value={config.tienda_horarios || ''}
                                            onChange={e => setConfig({ ...config, tienda_horarios: e.target.value })}
                                            placeholder="Lun-Sab 10:00-19:00"
                                            data-testid="input-tienda-horarios"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <Label className="form-label">Link de Google Maps (compartir desde la app)</Label>
                                        <Input
                                            value={config.google_maps_link || ''}
                                            onChange={e => setConfig({ ...config, google_maps_link: e.target.value })}
                                            placeholder="https://maps.app.goo.gl/..."
                                            data-testid="input-google-maps-link"
                                        />
                                        <p className="text-xs text-zinc-500 mt-1">En Google Maps -&gt; Compartir -&gt; Copiar link. Boton "Como llegar" en la web.</p>
                                    </div>
                                    <div className="form-group">
                                        <Label className="form-label">Embed src (iframe de Google Maps)</Label>
                                        <Input
                                            value={config.google_maps_embed || ''}
                                            onChange={e => setConfig({ ...config, google_maps_embed: e.target.value })}
                                            placeholder='https://www.google.com/maps/embed?pb=...'
                                            data-testid="input-google-maps-embed"
                                        />
                                        <p className="text-xs text-zinc-500 mt-1">En Maps -&gt; Compartir -&gt; Insertar mapa -&gt; copia SOLO la URL del src="..." del iframe.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t border-zinc-700 pt-4 mt-2">
                                <h4 className="text-sm font-semibold text-zinc-200 mb-3 flex items-center gap-2">
                                    <span className="text-pink-400">●</span> TikTok - Videos en la tienda
                                </h4>
                                <p className="text-xs text-zinc-500 mb-3">Pega URLs completas de TikTok. Se mostraran en la seccion "Clientes en TikTok" de la tienda publica.</p>
                                <div className="space-y-2">
                                    {(config.tiktok_videos || []).map((url, idx) => (
                                        <div key={idx} className="flex gap-2">
                                            <Input
                                                value={url}
                                                onChange={(e) => {
                                                    const arr = [...(config.tiktok_videos || [])];
                                                    arr[idx] = e.target.value;
                                                    setConfig({ ...config, tiktok_videos: arr });
                                                }}
                                                placeholder="https://www.tiktok.com/@usuario/video/123456..."
                                                className="font-mono text-xs"
                                                data-testid={`input-tiktok-${idx}`}
                                            />
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => {
                                                    const arr = (config.tiktok_videos || []).filter((_, i) => i !== idx);
                                                    setConfig({ ...config, tiktok_videos: arr });
                                                }}
                                                className="text-red-400"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    ))}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setConfig({ ...config, tiktok_videos: [...(config.tiktok_videos || []), ''] })}
                                        data-testid="btn-add-tiktok"
                                    >
                                        + Agregar video TikTok
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="card">
                        <CardContent className="p-5 space-y-4">
                            <h3 className="font-semibold text-zinc-200 flex items-center gap-2"><Image className="w-4 h-4 text-amber-400" /> Imagen del Hero</h3>
                            <input type="file" accept="image/*" ref={fileInputRef} onChange={handleUploadHeroImage} className="hidden" />
                            {config.hero_image ? (
                                <div className="space-y-3">
                                    <div className="relative rounded-xl overflow-hidden border border-zinc-700">
                                        <img src={`${API_URL}${config.hero_image}`} alt="Hero" className="w-full h-40 object-cover" />
                                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                            <div className="flex gap-2">
                                                <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="btn-change-hero-image">
                                                    <Upload className="w-3 h-3 mr-1" /> Cambiar
                                                </Button>
                                                <Button size="sm" variant="destructive" onClick={handleDeleteHeroImage} data-testid="btn-delete-hero-image">
                                                    <Trash2 className="w-3 h-3 mr-1" /> Quitar
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                                    className="w-full h-32 border-2 border-dashed border-zinc-700 rounded-xl flex flex-col items-center justify-center gap-2 text-zinc-500 hover:border-amber-400 hover:text-amber-400 transition-colors"
                                    data-testid="btn-upload-hero-image">
                                    {uploading ? (
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-400" />
                                    ) : (
                                        <>
                                            <Upload className="w-6 h-6" />
                                            <span className="text-xs">Subir imagen de fondo</span>
                                            <span className="text-[10px] text-zinc-600">JPG, PNG o WebP</span>
                                        </>
                                    )}
                                </button>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="card">
                        <CardContent className="p-5 space-y-4">
                            <h3 className="font-semibold text-zinc-200">Opciones</h3>
                            <div className="flex items-center justify-between">
                                <Label className="text-sm text-zinc-400">Animaciones</Label>
                                <Switch checked={config.animaciones !== false} onCheckedChange={v => setConfig({ ...config, animaciones: v })} />
                            </div>
                            <div className="form-group"><Label className="form-label">Layout del catalogo</Label>
                                <Select value={config.layout_catalogo || 'grid-4'} onValueChange={v => setConfig({ ...config, layout_catalogo: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="grid-2">2 columnas</SelectItem>
                                        <SelectItem value="grid-3">3 columnas</SelectItem>
                                        <SelectItem value="grid-4">4 columnas</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Right: Live Preview */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-zinc-200">Vista previa</h3>
                        <div className="flex gap-1 bg-zinc-800 rounded-lg p-1">
                            <button onClick={() => setPreviewDevice('desktop')} className={`p-1.5 rounded-md transition-colors ${previewDevice === 'desktop' ? 'bg-zinc-700 text-amber-400' : 'text-zinc-500'}`}>
                                <Monitor className="w-4 h-4" />
                            </button>
                            <button onClick={() => setPreviewDevice('mobile')} className={`p-1.5 rounded-md transition-colors ${previewDevice === 'mobile' ? 'bg-zinc-700 text-amber-400' : 'text-zinc-500'}`}>
                                <Smartphone className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <div className={`mx-auto border border-zinc-700 rounded-xl overflow-hidden shadow-2xl transition-all duration-300 ${previewDevice === 'mobile' ? 'max-w-[320px]' : 'w-full'}`}>
                        <div style={{ background: t.colors.bg, color: t.colors.text, fontFamily: t.font }} className="min-h-[500px]">
                            {/* Preview Navbar */}
                            <div className="px-4 py-3 flex items-center justify-between" style={{ background: t.colors.navBg, borderBottom: `1px solid ${t.colors.border}` }}>
                                <span className="text-sm font-bold tracking-wider" style={{ fontFamily: t.font }}>KARRY JEAN'S</span>
                                <div className="flex gap-3">
                                    <div className="w-4 h-4 rounded-full" style={{ background: t.colors.accent, opacity: 0.5 }} />
                                    <div className="w-4 h-4 rounded-full" style={{ background: t.colors.accent, opacity: 0.5 }} />
                                </div>
                            </div>

                            {/* Preview Banner */}
                            {config.banner_text && (
                                <div className="py-1.5 text-center text-[10px] tracking-widest font-bold" style={{ background: t.colors.accent, color: t.colors.badgeText }}>
                                    {config.banner_text}
                                </div>
                            )}

                            {/* Preview Hero */}
                            <div className="px-6 py-10 text-center relative" style={{ background: t.hero?.gradient || 'none' }}>
                                {config.hero_image && (
                                    <div className="absolute inset-0">
                                        <img src={`${API_URL}${config.hero_image}`} alt="" className="w-full h-full object-cover opacity-30" />
                                    </div>
                                )}
                                <p className="text-[9px] tracking-[0.25em] uppercase mb-2 relative" style={{ color: t.colors.accent }}>
                                    {config.hero_tagline || t.hero.tagline}
                                </p>
                                <h1 className="text-3xl font-bold tracking-wider leading-tight mb-2 relative" style={{ fontFamily: t.font }}>
                                    {config.hero_title_1 || t.hero.title1}<br />
                                    <span style={{ color: t.colors.accent }}>{config.hero_title_2 || t.hero.title2}</span>
                                </h1>
                                <p className="text-xs mb-4 opacity-70 relative">{config.hero_subtitle || t.hero.subtitle}</p>
                                <span className="inline-block px-5 py-2 text-xs font-semibold relative" style={{
                                    background: t.colors.accent, color: t.colors.badgeText,
                                    borderRadius: t.buttonRadius
                                }}>
                                    {config.hero_cta || t.hero.cta}
                                </span>
                            </div>

                            {/* Preview Cards */}
                            <div className="px-4 pb-6">
                                <p className="text-xs tracking-wider font-bold mb-3" style={{ fontFamily: t.font }}>CATALOGO</p>
                                <div className={`grid gap-2 ${previewDevice === 'mobile' ? 'grid-cols-2' : 'grid-cols-3'}`}>
                                    {[1, 2, 3].map(i => (
                                        <div key={i} style={{ background: t.colors.surface, border: `1px solid ${t.colors.border}`, borderRadius: t.cardRadius }} className="overflow-hidden">
                                            <div className="aspect-[3/4] relative" style={{ background: `linear-gradient(135deg, ${t.colors.surface}, ${t.colors.bg})` }}>
                                                <div className="absolute top-2 left-2 px-2 py-0.5 text-[8px] font-bold" style={{ background: t.colors.badge, color: t.colors.badgeText, borderRadius: t.buttonRadius }}>NEW</div>
                                            </div>
                                            <div className="p-2">
                                                <div className="h-2 w-16 rounded-full mb-1" style={{ background: t.colors.border }} />
                                                <div className="h-2 w-10 rounded-full" style={{ background: t.colors.accent, opacity: 0.6 }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Preview Footer */}
                            <div className="px-4 py-4 text-center" style={{ borderTop: `1px solid ${t.colors.border}` }}>
                                <p className="text-[9px] tracking-wider opacity-40">KARRY JEAN'S &copy; 2026</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
